import { Role } from 'shared/@types'
import { CountryCode } from 'shared/@types'
import { BuyerProfile } from 'shared/@types'
import { Coordinate } from 'shared/@types'
import { LangMap } from 'shared/library/routes/define-route'
import type { Money, Price, LocalizedPrice } from 'shared/@types'
export interface BreedMatch {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  completed_at: string | null
  user_id: number | null
  breed_id: number | null
  preferences: string | null
  language: string
  country: string
  animal: Animal
  traits: Array<unknown> | null
  confidence: number | null
  // mutators
  is_confident: boolean
  reference: string
  // overrides
  responses: {question: string; options: string[]; answer: string|null}[]
  // relations
  user: User
  breed: Breed
  breed_match_items: BreedMatchItem[]
  // counts
  breed_match_items_count?: number
}

export interface DogLicenceModule {
  // columns
  id: number
  user_id: number
  module: number
  answers: Array<unknown>
  passed_at: string | null
  created_at: string | null
  updated_at: string | null
  // relations
  user: User
  // counts
}

export interface OneTimeCampaignRecipient {
  // columns
  id: number
  one_time_campaign_id: number
  user_id: number
  channel: string
  queued_at: string | null
  sent_at: string | null
  created_at: string | null
  updated_at: string | null
  // relations
  campaign: OneTimeCampaign
  user: User
  // counts
}

export interface MarketingSpend {
  // columns
  id: number
  month: string
  platform: MarketingPlatform
  segment: MarketingSpendSegment
  amount_minor: number
  currency: string
  created_at: string | null
  updated_at: string | null
}

export interface Breeder {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  slug: string
  name: string
  animal: Animal
  affiliation: BreederAffiliation | null
  established_year: string | null
  description: string | null
  instagram: string | null
  facebook: string | null
  tiktok: string | null
  website: string | null
  color: string | null
  // overrides
  average_response_time_minutes: number | null
  // relations
  breeds: Breed[]
  owners: User[]
  breeder_pets: BreederPet[]
  breeder_posts: BreederPost[]
  active_listings: Listing[]
  subscribers: User[]
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  view_count: ViewCount | null
  users: User[]
  likes: Like[]
  // counts
  breeds_count?: number
  owners_count?: number
  breeder_pets_count?: number
  breeder_posts_count?: number
  active_listings_count?: number
  subscribers_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
  views_count?: number
  users_count?: number
  likes_count?: number
}

export interface Product {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  brand_id: number | null
  domain: Domain
  rating_internal_id: string | null
  rating_score: number | null
  rating_votes: number
  rating_reviews: Record<string, unknown> | null
  rating_synced_at: string | null
  // mutators
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  reference: string
  // overrides
  slug: LangMap
  name: string
  summary: string
  description: string
  // relations
  product_categories: ProductCategory[]
  brand: Brand
  product_variants: ProductVariant[]
  order_items: OrderItem[]
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  visible_reviews: Review[]
  aggregate_reviews: Review[]
  reviews: Review[]
  internal_reviews: Review[]
  view_count: ViewCount | null
  users: User[]
  likes: Like[]
  // counts
  product_categories_count?: number
  product_variants_count?: number
  order_items_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
  visible_reviews_count?: number
  aggregate_reviews_count?: number
  reviews_count?: number
  internal_reviews_count?: number
  views_count?: number
  users_count?: number
  likes_count?: number
}

export interface Order {
  // columns
  pricing: LocalizedPrice
  shipping_fee: LocalizedPrice
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  session_token: string
  status: OrderStatus
  domain: Domain
  email: string | null
  name: string | null
  phone_number: string | null
  street_address: string | null
  city: string | null
  postal_code: string | null
  country: string | null
  tracking_number: string | null
  tracking_url: string | null
  shipped_at: string | null
  delivered_at: string | null
  // mutators
  reference: string
  // relations
  order_items: OrderItem[]
  payments: Payment[]
  completed_payments: Payment[]
  // counts
  order_items_count?: number
  payments_count?: number
  completed_payments_count?: number
}

export interface Post {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  published_at: string | null
  featured_order: number | null
  user_id: number
  // mutators
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  // overrides
  reading_time: number
  slug: LangMap
  title: string
  summary: string
  body: string
  // relations
  tags: Tag[]
  user: User
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  tags_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface BreedQuestion {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  breed_id: number
  // mutators
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  // overrides
  slug: LangMap
  question: string
  answer: string
  // relations
  breed: Breed
  // counts
}

export interface PushToken {
  // columns
  id: number
  user_id: number
  token: string
  device_id: string | null
  platform: string | null
  last_used_at: string | null
  created_at: string | null
  updated_at: string | null
  // relations
  user: User
  // counts
}

export interface Review {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  reviewable_type: string
  reviewable_id: number
  source: string
  external_id: string | null
  rating_count: number
  rating_score: number | null
  reviewer_name: string | null
  text: string | null
  // relations
  reviewable: Review
  // counts
}

export interface MediaAsset {
  focus_area?: [number, number, number, number] | null
  image_versions?: Partial<Record<'avatar' | 'card' | 'large' | 'full' | 'square' | 'portrait', Array<{ width: number; height: number | null; url: string }>>> | []
  // columns
  id?: number
  uuid: string
  created_at: string | null
  updated_at?: string | null
  owner_id?: number | null
  model_type?: string | null
  model_id?: number | null
  type: MediaAssetType
  source_format?: string | null
  description: string | null
  legacy_url?: string | null
  display_weight?: number
  analysis_score?: number | null
  analysis_data?: Array<unknown> | null
  impressions?: number
  clicks?: number
  // mutators
  url: string
  video_url: string
  reference?: string
  // relations
  owner: User
  model: Contract|BreederPost|Pet|Breed|Listing|User|BreederPet|Breeder|Post
  breed_preferences: BreedMediaAssetPreference[]
  // counts
  breed_preferences_count?: number
}

export interface BreederPet {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  breeder_id: number
  breed_id: number
  name: string
  gender: string
  birth_date: string
  description: string | null
  // mutators
  reference: string
  // relations
  breeder: Breeder
  breed: Breed
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface TypesMock {
  // mutators
  characteristic: Characteristic
  stat_type: StatType
  delete_reason: DeleteReason
  pet_status: PetStatus
}

export interface Tag {
  // columns
  id: number
  type: TagType
  featured_order: number | null
  // mutators
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  // overrides
  slug: LangMap
  name: string
  description: string
  // relations
  posts: Post[]
  // counts
  posts_count?: number
}

export interface PetPurchase {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  deposit_paid_at: string | null
  deposit_withdrawn_at: string | null
  final_payment_paid_at: string | null
  final_payment_withdrawn_at: string | null
  delivery_confirmed_at: string | null
  pet_id: number
  seller_id: number
  buyer_id: number
  price: number
  deposit: number
  currency: string
  booked_insurance_call: boolean | null
  // mutators
  reference: string
  // relations
  seller: User
  buyer: User
  pet: Pet
  listing: Listing
  contract: Contract
  insurance_call: InsuranceCall | null
  view_count: ViewCount | null
  payments: Payment[]
  completed_payments: Payment[]
  // counts
  views_count?: number
  payments_count?: number
  completed_payments_count?: number
}

export interface OrderItem {
  // columns
  unit_price: LocalizedPrice
  id: number
  created_at: string | null
  updated_at: string | null
  order_id: number
  product_variant_id: number
  quantity: number
  // relations
  order: Order
  product_variant: ProductVariant
  // counts
}

export interface User {
  // columns
  id: number
  uuid: string
  created_at?: string | null
  updated_at?: string | null
  verified_at: string | null
  deleted_at?: string | null
  remember_token?: string | null
  password?: string | null
  identity_number?: string | null
  birth_date: string | null
  first_name?: string | null
  last_name?: string | null
  street_address?: string | null
  city: string | null
  postal_code?: string | null
  language: string
  email?: string | null
  phone_number?: string | null
  accepts_data_sharing?: boolean | null
  accepts_marketing?: boolean | null
  breeder_id: number | null
  stripe_account_id?: string | null
  stripe_account_status?: StripeAccountStatus | null
  banned_at?: string | null
  ban_reason?: string | null
  dog_licence_completed_at?: string | null
  // mutators
  name: string
  display_name: string
  is_trusted: boolean
  reference: string
  // overrides
  approximate_location: Coordinate
  buyer_profile: BuyerProfile
  country: CountryCode
  location: {coordinates: [number, number], type: string}
  is_breeder: boolean
  onboarding_role: Role | null
  average_response_time_minutes: number | null
  // relations
  breeder: Breeder
  listings: Listing[]
  likes: Like[]
  searches: Search[]
  conversations_as_buyer: Conversation[]
  conversations_as_seller: Conversation[]
  posts: Post[]
  push_tokens: PushToken[]
  payments: Payment[]
  pet_purchases: PetPurchase[]
  pets: Pet[]
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  listings_count?: number
  likes_count?: number
  searches_count?: number
  conversations_as_buyer_count?: number
  conversations_as_seller_count?: number
  posts_count?: number
  push_tokens_count?: number
  payments_count?: number
  pet_purchases_count?: number
  pets_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface MarketingTouchpoint {
  // columns
  id: number
  user_id: number | null
  anonymous_id: string | null
  session_id: string | null
  event_type: string
  source: string | null
  medium: string | null
  campaign: string | null
  term: string | null
  content: string | null
  channel: string | null
  platform: string | null
  landing_page: string | null
  current_page: string | null
  referrer: string | null
  gclid: string | null
  gbraid: string | null
  wbraid: string | null
  fbclid: string | null
  msclkid: string | null
  ttclid: string | null
  occurred_at: string
  payload: Array<unknown> | null
  created_at: string | null
  updated_at: string | null
  // relations
  user: User | null
  // counts
}

export interface InsuranceCall {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  pet_purchase_id: number | null
  user_id: number | null
  status: InsuranceCallStatus | null
  scheduled_at: string | null
  customer: Array<unknown> | null
  // relations
  user: User
  pet_purchase: PetPurchase | null
  // counts
}

export interface MarketingConversion {
  // columns
  id: number
  conversion_type: MarketingConversionType
  converted_type: string | null
  converted_id: number | null
  user_id: number | null
  anonymous_id: string | null
  session_id: string | null
  attribution_model: string
  source: string
  medium: string | null
  campaign: string | null
  term: string | null
  content: string | null
  channel: string
  platform: string | null
  landing_page: string | null
  current_page: string | null
  referrer: string | null
  gclid: string | null
  gbraid: string | null
  wbraid: string | null
  fbclid: string | null
  msclkid: string | null
  ttclid: string | null
  occurred_at: string
  first_touch: Array<unknown> | null
  last_touch: Array<unknown> | null
  last_non_direct_touch: Array<unknown> | null
  raw_attribution: Array<unknown> | null
  created_at: string | null
  updated_at: string | null
  // relations
  converted: MarketingConversion
  user: User | null
  // counts
}

export interface Listing {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  verified_at: string | null
  published_at: string | null
  user_id: number
  slug_prefix: string
  slug: string
  animal: Animal | null
  price: number | null
  deposit: number | null
  currency: string | null
  birth_date: string | null
  breed_id: number | null
  mixed_breed_name: string | null
  description: string | null
  litter_insurance_company: LitterInsuranceCompany | null
  trygghansa_litter_insurance_lead_sent_at: string | null
  verification_status: VerificationStatus | null
  delete_reason: DeleteReason | null
  hidden_from_breeder_profile_at: string | null
  // mutators
  breed_name: string
  title: string
  breed_type: BreedType
  pet_age: PetAge
  // overrides
  short_description: string
  is_insured: boolean
  pickup_date: string
  analysis: ListingAnalysis | null
  // relations
  user: User
  breed: Breed
  conversations: Conversation[]
  litter_insurance: LitterInsurance
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  pets: Pet[]
  view_count: ViewCount | null
  users: User[]
  likes: Like[]
  payments: Payment[]
  completed_payments: Payment[]
  // counts
  conversations_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
  pets_count?: number
  views_count?: number
  users_count?: number
  likes_count?: number
  payments_count?: number
  completed_payments_count?: number
}

export interface OneTimeCampaign {
  // columns
  id: number
  segment: UserSegment
  channel: NotificationChannel
  country: string | null
  subject: string | null
  preheader: string | null
  header_image_url: string | null
  header_image_link_url: string | null
  body: string
  scheduled_at: string | null
  queued_at: string | null
  recipients_prepared_at: string | null
  sent_at: string | null
  sent_to: number | null
  created_at: string | null
  updated_at: string | null
  // relations
  recipients: OneTimeCampaignRecipient[]
  // counts
  recipients_count?: number
}

export interface Search {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  user_id: number
  name: string
  animal: Animal | null
  breed_type: BreedType | null
  gender: Gender | null
  pet_age: PetAge | null
  only_breeders: boolean | null
  max_price: number | null
  max_distance_in_km: number | null
  latitude: number | null
  longitude: number | null
  country: string | null
  notify: boolean
  // mutators
  reference: string
  // relations
  user: User
  search_breeds: SearchBreed[]
  search_matches: SearchMatch[]
  unseen_search_matches: SearchMatch[]
  // counts
  search_breeds_count?: number
  search_matches_count?: number
  unseen_search_matches_count?: number
}

export interface Like {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  user_id: number
  model_type: string
  model_id: number
  // relations
  user: User
  model: Breed|Listing|Breeder|Product
  // counts
}

export interface Payment {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  completed_at: string | null
  refunded_at: string | null
  user_id: number | null
  model_type: string
  model_id: number
  amount_minor_sek: number
  amount_minor: number
  currency: string
  coupon_id: number | null
  stripe_id: string
  stripe_secret: string
  stripe_status: PaymentStatus
  // mutators
  product_titles: unknown
  reference: string
  // relations
  user: User | null
  model: Listing|PetPurchase|Order
  payment_items: PaymentItem[]
  coupon: Coupon
  // counts
  payment_items_count?: number
}

export interface BreedMediaAssetPreference {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  breed_id: number
  media_asset_id: number
  status: BreedMediaAssetPreferenceStatus
  // relations
  breed: Breed
  media_asset: MediaAsset
  // counts
}

export interface LitterInsurance {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  submitted_at: string | null
  approved_at: string | null
  manually_created_at: string | null
  user_id: number
  listing_id: number
  mother_pet_id: number | null
  status: LitterInsuranceStatus
  mom_name: string
  mom_registration_id: string
  mom_breed: string
  mom_birth_date: string
  males: number | null
  females: number | null
  // mutators
  reference: string
  // relations
  user: User
  listing: Listing
  mother: Pet | null
  // counts
}

export interface SearchMatch {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  seen_at: string | null
  search_id: number
  listing_id: number
  // relations
  search: Search
  listing: Listing
  // counts
}

export interface PaymentItem {
  // columns
  id: number
  payment_id: number
  product_type: MarketplaceProductType
  amount_minor: number
  expired_at: string | null
  // overrides
  data: Record<string, number>
  // relations
  payment: Payment
  // counts
}

export interface ProductVariant {
  // columns
  pricing: LocalizedPrice
  recommended_pricing: LocalizedPrice
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  product_id: number
  ean: string
  sku: string | null
  in_stock: number
  available: boolean
  meta: Array<unknown> | null
  // mutators
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  reference: string
  // overrides
  name: string
  // relations
  order_items: OrderItem[]
  product: Product
  bundle_variants: Array<ProductVariant & { pivot: { quantity: number; sort_order: number } }>
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  order_items_count?: number
  bundle_variants_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface ViewCount {
  // columns
  model_type: string
  model_id: number
  count: number
  // relations
  model: unknown
  // counts
}

export interface SearchBreed {
  // columns
  id: number
  search_id: number
  breed_id: number | null
  query: string | null
  // relations
  search: Search
  breed: Breed
  // counts
}

export interface Breed {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  animal: Animal
  country: string
  breed_type: BreedType
  breed_group: number | null
  aggressiveness: number
  child_friendly: number
  energy: number
  hair_shedding: number
  health: number
  intelligence: number
  grooming_needs: number
  learning_ability: number
  barking_level: number
  height_male_min: number
  height_male_max: number
  height_female_min: number
  height_female_max: number
  weight_male_min: number
  weight_male_max: number
  weight_female_min: number
  weight_female_max: number
  life_expectancy_min: number
  life_expectancy_max: number
  // mutators
  country_name: string
  has_correct_content_structure: boolean
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  // overrides
  characteristics: Characteristic[]
  sources: {name: string; url: string;}[]
  slug: LangMap
  name: string
  summary: string
  body: string
  // relations
  breeders: Breeder[]
  listings: Listing[]
  active_listings: Listing[]
  active_pets: Pet[]
  breed_questions: BreedQuestion[]
  breed_media_asset_preferences: BreedMediaAssetPreference[]
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  users: User[]
  likes: Like[]
  // counts
  breeders_count?: number
  listings_count?: number
  active_listings_count?: number
  active_pets_count?: number
  breed_questions_count?: number
  breed_media_asset_preferences_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
  users_count?: number
  likes_count?: number
}

export interface Conversation {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  seller_id: number
  buyer_id: number
  listing_id: number | null
  breeder_id: number | null
  first_seller_response_at: string | null
  buyer_last_read_message_id: number | null
  seller_last_read_message_id: number | null
  last_message_at: string | null
  // mutators
  reference: string
  // relations
  messages: ConversationMessage[]
  last_message: ConversationMessage
  listing: Listing | null
  breeder: Breeder | null
  seller: User
  buyer: User
  message_annotations: MessageAnnotation[]
  // counts
  messages_count?: number
  message_annotations_count?: number
}

export interface PetInsurance {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  user_id: number | null
  pet_id: number | null
  subscription: Array<unknown>
  // relations
  user: User
  pet: Pet
  // counts
}

export interface ListingAnalysis {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  listing_id: number
  data: Array<unknown> | null
  // relations
  listing: Listing
  // counts
}

export interface OneTimePassword {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  identifier_type: string
  identifier: string
  password?: string
}

export interface Pet {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  listing_id: number
  role: PetRole
  name: string
  status: PetStatus
  gender: Gender
  description: string | null
  registration_id: string | null
  deceased: boolean
  // mutators
  available: boolean
  reference: string
  // relations
  listing: Listing
  pet_purchase: PetPurchase
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface Coupon {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  code: string
  discount_percentage: number | null
  discount_amount_minor: number | null
  currency: string | null
  max_uses_per_user: number | null
  expired_at: string
}

export interface BreedMatchItem {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  breed_match_id: number
  breed_id: number
  similarity: number
  description: string | null
  // relations
  breed_match: BreedMatch
  breed: Breed
  // counts
}

export interface Setting {
  // columns
  id: number
  key: string
  value: Array<unknown>
}

export interface BreederPost {
  // columns
  id: number
  created_at: string | null
  updated_at: string | null
  breeder_id: number
  slug: string
  title: string
  body: string
  // relations
  breeder: Breeder
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface Contract {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  pet_purchase_id: number
  pet_is_hidden_defects_insured: boolean | null
  pet_is_vaccinated: boolean | null
  pet_is_dewormed: boolean | null
  pet_is_neutered: boolean | null
  pet_previous_injuries: string | null
  pet_veterinary_examination_date: string | null
  pet_delivery_date: string | null
  mom_name: string | null
  mom_breed: string | null
  mom_registration_id: string | null
  dad_name: string | null
  dad_breed: string | null
  dad_registration_id: string | null
  seller_signed_at: string | null
  buyer_signed_at: string | null
  data_snapshot?: Array<unknown> | null
  data_hash?: string | null
  // mutators
  reference: string
  // overrides
  signed_at: string
  // relations
  pet_purchase: PetPurchase
  seller: User
  buyer: User
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  owned_media_assets_count?: number
  media_assets_count?: number
}

export interface MessageAnnotation {
  // columns
  id: number
  conversation_id: number
  message_id: string
  sender_user_uuid: string | null
  actor_role: ConversationActorRole
  annotation_type: MessageAnnotationType
  label: MessageAnnotationLabel
  confidence: number
  value: Array<unknown> | null
  message_sent_at: string | null
  created_at: string | null
  updated_at: string | null
  // relations
  conversation: Conversation
  // counts
}

export interface ConversationMessage {
  // columns
  id: number
  conversation_id: number
  sender_id: number | null
  body: string | null
  type: string
  client_id: string | null
  legacy_message_id: string | null
  image_disk: string | null
  image_path: string | null
  image_filename: string | null
  image_mime_type: string | null
  image_size: number | null
  effects_dispatched_at: string | null
  created_at: string | null
  updated_at: string | null
  // relations
  conversation: Conversation
  sender: User
  // counts
}

export interface UserBlock {
  // columns
  id: number
  user_id: number
  blocked_user_id: number
  created_at: string | null
  updated_at: string | null
}

export interface Brand {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  name: string
  // mutators
  reference: string
  // relations
  products: Product[]
  // counts
  products_count?: number
}

export interface ProductCategory {
  // columns
  id: number
  uuid: string
  created_at: string | null
  updated_at: string | null
  sort_order: number
  // mutators
  interfaces: Array<unknown>
  incomplete_translations: Array<unknown>
  incomplete_translations_text: string
  translation_complete: boolean
  translatable_columns_from: Array<unknown>
  translations: unknown
  reference: string
  // overrides
  slug: LangMap
  name: string
  summary: string
  description: string
  // relations
  products: Product[]
  owned_media_assets: MediaAsset[]
  media_assets: MediaAsset[]
  avatar: MediaAsset | null
  // counts
  products_count?: number
  owned_media_assets_count?: number
  media_assets_count?: number
}

export const Animal = {
  Dog: 'dog',
  Cat: 'cat',
} as const;

export type Animal = typeof Animal[keyof typeof Animal]

export const MarketingPlatform = {
  Meta: 'meta',
  Google: 'google',
  Sms: 'sms',
} as const;

export type MarketingPlatform = typeof MarketingPlatform[keyof typeof MarketingPlatform]

export const MarketingSpendSegment = {
  Buyer: 'buyer',
  Seller: 'seller',
  Shared: 'shared',
} as const;

export type MarketingSpendSegment = typeof MarketingSpendSegment[keyof typeof MarketingSpendSegment]

export const BreederAffiliation = {
  IndependentBreeder: 'ib',
  SwedishKennelClub: 'skk',
  NorwegianKennelClub: 'nkk',
  NorskeRasekattklubbersRiksforbund: 'nrr',
  DanishKennelClub: 'dkk',
  FelisDanica: 'fd',
  FinnishKennelClub: 'skl',
  RescueOrganization: 'ro',
  Sverak: 'svrk',
} as const;

export type BreederAffiliation = typeof BreederAffiliation[keyof typeof BreederAffiliation]

export const Domain = {
  Marketplace: 'marketplace',
  Shop: 'shop',
} as const;

export type Domain = typeof Domain[keyof typeof Domain]

export const OrderStatus = {
  Draft: 'draft',
  PendingPayment: 'pending_payment',
  Paid: 'paid',
  Canceled: 'canceled',
  Fulfilled: 'fulfilled',
} as const;

export type OrderStatus = typeof OrderStatus[keyof typeof OrderStatus]

export const MediaAssetType = {
  Image: 'image',
  Video: 'video',
  Pdf: 'pdf',
  Signature: 'signature',
} as const;

export type MediaAssetType = typeof MediaAssetType[keyof typeof MediaAssetType]

export const Characteristic = {
  GreatWithKids: 'great_with_kids',
  HighEnergy: 'high_energy',
  SuperSmart: 'super_smart',
  LowGrooming: 'low_grooming',
  Protective: 'protective',
  Quiet: 'quiet',
  EasyToTrain: 'easy_to_train',
  Stubborn: 'stubborn',
  SizeTiny: 'size_tiny',
  SizeSmall: 'size_small',
  SizeMedium: 'size_medium',
  SizeLarge: 'size_large',
  SizeGiant: 'size_giant',
} as const;

export type Characteristic = typeof Characteristic[keyof typeof Characteristic]

export const StatType = {
  Impression: 'impression',
  Click: 'click',
} as const;

export type StatType = typeof StatType[keyof typeof StatType]

export const DeleteReason = {
  SoldViaUs: 'sold_via_us',
  SoldPrivately: 'sold_privately',
  SoldOnAnotherPlatform: 'sold_on_another_platform',
  KeepingThePet: 'keeping_the_pet',
  NoLongerAvailable: 'no_longer_available',
  MistakenListing: 'mistaken_listing',
  Other: 'other',
} as const;

export type DeleteReason = typeof DeleteReason[keyof typeof DeleteReason]

export const PetStatus = {
  Available: 'available',
  Reserved: 'reserved',
  Sold: 'sold',
} as const;

export type PetStatus = typeof PetStatus[keyof typeof PetStatus]

export const TagType = {
  Blog: 'blog',
  Faq: 'faq',
} as const;

export type TagType = typeof TagType[keyof typeof TagType]

export const StripeAccountStatus = {
  NotCreated: 'not_created',
  PendingOnboarding: 'pending_onboarding',
  PendingReview: 'pending_review',
  Active: 'active',
  PayoutsDisabled: 'payouts_disabled',
  Closed: 'closed',
} as const;

export type StripeAccountStatus = typeof StripeAccountStatus[keyof typeof StripeAccountStatus]

export const InsuranceCallStatus = {
  Declined: 'declined',
  Scheduled: 'scheduled',
  Completed: 'completed',
  Cancelled: 'cancelled',
} as const;

export type InsuranceCallStatus = typeof InsuranceCallStatus[keyof typeof InsuranceCallStatus]

export const MarketingConversionType = {
  BuyerConversationListing: 'buyer_conversation_listing',
  BuyerConversationBreeder: 'buyer_conversation_breeder',
  SellerListingCreated: 'seller_listing_created',
  SellerListingPublished: 'seller_listing_published',
  SellerBreederCreated: 'seller_breeder_created',
} as const;

export type MarketingConversionType = typeof MarketingConversionType[keyof typeof MarketingConversionType]

export const LitterInsuranceCompany = {
  SE_Agria: 'se.agria',
  SE_Folksam: 'se.folksam',
  SE_IfSkadeforsakring: 'se.if',
  SE_Svedea: 'se.svedea',
  SE_Sveland: 'se.sveland',
  SE_TryggHansa: 'se.trygg_hansa',
  Other: 'other',
  None: 'none',
} as const;

export type LitterInsuranceCompany = typeof LitterInsuranceCompany[keyof typeof LitterInsuranceCompany]

export const VerificationStatus = {
  Pending: 'pending',
  Verified: 'verified',
  Denied: 'denied',
} as const;

export type VerificationStatus = typeof VerificationStatus[keyof typeof VerificationStatus]

export const BreedType = {
  Purebred: 'purebred',
  Crossbreed: 'crossbreed',
  MixedBreed: 'mixed_breed',
} as const;

export type BreedType = typeof BreedType[keyof typeof BreedType]

export const PetAge = {
  Newborn: 'newborn',
  Young: 'young',
  Adult: 'adult',
} as const;

export type PetAge = typeof PetAge[keyof typeof PetAge]

export const UserSegment = {
  AllUsers: 'all_users',
  Sellers: 'sellers',
  Buyers: 'buyers',
  Breeders: 'breeders',
} as const;

export type UserSegment = typeof UserSegment[keyof typeof UserSegment]

export const NotificationChannel = {
  Email: 'email',
  Sms: 'sms',
  Push: 'push',
} as const;

export type NotificationChannel = typeof NotificationChannel[keyof typeof NotificationChannel]

export const Gender = {
  Male: 'male',
  Female: 'female',
} as const;

export type Gender = typeof Gender[keyof typeof Gender]

export const PaymentStatus = {
  RequiresPaymentMethod: 'requires_payment_method',
  RequiresConfirmation: 'requires_confirmation',
  RequiresAction: 'requires_action',
  Processing: 'processing',
  RequiresCapture: 'requires_capture',
  Canceled: 'canceled',
  Succeeded: 'succeeded',
} as const;

export type PaymentStatus = typeof PaymentStatus[keyof typeof PaymentStatus]

export const BreedMediaAssetPreferenceStatus = {
  Preferred: 'preferred',
  Hidden: 'hidden',
} as const;

export type BreedMediaAssetPreferenceStatus = typeof BreedMediaAssetPreferenceStatus[keyof typeof BreedMediaAssetPreferenceStatus]

export const LitterInsuranceStatus = {
  Applied: 'applied',
  Submitted: 'submitted',
  Approved: 'approved',
  Denied: 'denied',
  PetsDataCollectedAndSubmitted: 'pets_data_collected_and_submitted',
} as const;

export type LitterInsuranceStatus = typeof LitterInsuranceStatus[keyof typeof LitterInsuranceStatus]

export const MarketplaceProductType = {
  ListingPublishDog: 'listing.publish_dog',
  ListingPublishCat: 'listing.publish_cat',
  BoostSmall: 'boost.small',
  BoostMedium: 'boost.medium',
  BoostLarge: 'boost.large',
  BoostUltra: 'boost.ultra',
  PetPayDeposit: 'petpay.deposit',
  PetPayFinalPayment: 'petpay.final_payment',
} as const;

export type MarketplaceProductType = typeof MarketplaceProductType[keyof typeof MarketplaceProductType]

export const PetRole = {
  Offspring: 'offspring',
  Mother: 'mother',
  Father: 'father',
} as const;

export type PetRole = typeof PetRole[keyof typeof PetRole]

export const ConversationActorRole = {
  Buyer: 'buyer',
  Seller: 'seller',
  Unknown: 'unknown',
} as const;

export type ConversationActorRole = typeof ConversationActorRole[keyof typeof ConversationActorRole]

export const MessageAnnotationType = {
  TransactionIntent: 'transaction_intent',
  PetInterest: 'pet_interest',
  Logistics: 'logistics',
  Commercial: 'commercial',
  TrustAndSafety: 'trust_and_safety',
  AnimalWelfare: 'animal_welfare',
  ServiceOpportunity: 'service_opportunity',
  ConversationOutcome: 'conversation_outcome',
  UserSentiment: 'user_sentiment',
} as const;

export type MessageAnnotationType = typeof MessageAnnotationType[keyof typeof MessageAnnotationType]

export const MessageAnnotationLabel = {
  GeneralInterest: 'general_interest',
  AsksIfAvailable: 'asks_if_available',
  SeriousInterest: 'serious_interest',
  WantsToMeet: 'wants_to_meet',
  MeetingProposed: 'meeting_proposed',
  MeetingConfirmed: 'meeting_confirmed',
  PurchaseIntent: 'purchase_intent',
  PurchaseAgreed: 'purchase_agreed',
  Reserved: 'reserved',
  CompletedLikely: 'completed_likely',
  BackedOut: 'backed_out',
  PetNoLongerAvailable: 'pet_no_longer_available',
  AsksAge: 'asks_age',
  AsksBreed: 'asks_breed',
  AsksGender: 'asks_gender',
  AsksSize: 'asks_size',
  AsksTemperament: 'asks_temperament',
  AsksHealth: 'asks_health',
  AsksVaccination: 'asks_vaccination',
  AsksPedigree: 'asks_pedigree',
  AsksParentAnimals: 'asks_parent_animals',
  AsksCompatibilityChildren: 'asks_compatibility_children',
  AsksCompatibilityPets: 'asks_compatibility_pets',
  AsksLocation: 'asks_location',
  SharesLocation: 'shares_location',
  DiscussesPickup: 'discusses_pickup',
  DiscussesDelivery: 'discusses_delivery',
  DateProposed: 'date_proposed',
  TimeProposed: 'time_proposed',
  LocationProposed: 'location_proposed',
  MeetingDetailsConfirmed: 'meeting_details_confirmed',
  AsksPrice: 'asks_price',
  PriceNegotiation: 'price_negotiation',
  PriceAccepted: 'price_accepted',
  DepositDiscussion: 'deposit_discussion',
  PaymentMethodDiscussion: 'payment_method_discussion',
  ContractDiscussion: 'contract_discussion',
  InsuranceDiscussion: 'insurance_discussion',
  OffPlatformRequest: 'off_platform_request',
  SuspiciousPayment: 'suspicious_payment',
  PressureOrUrgency: 'pressure_or_urgency',
  InconsistentInformation: 'inconsistent_information',
  PossibleScam: 'possible_scam',
  Harassment: 'harassment',
  AbusiveLanguage: 'abusive_language',
  PersonalDataShared: 'personal_data_shared',
  AsksAboutHomeEnvironment: 'asks_about_home_environment',
  AsksAboutExperience: 'asks_about_experience',
  DiscussesAnimalNeeds: 'discusses_animal_needs',
  ResponsibleBuyerSignal: 'responsible_buyer_signal',
  ResponsibleSellerSignal: 'responsible_seller_signal',
  WelfareRedFlag: 'welfare_red_flag',
  NeedsInsurance: 'needs_insurance',
  NeedsTransport: 'needs_transport',
  NeedsVetCheck: 'needs_vet_check',
  NeedsContract: 'needs_contract',
  NeedsPaymentHelp: 'needs_payment_help',
  NeedsStarterKit: 'needs_starter_kit',
  NeedsTrainingAdvice: 'needs_training_advice',
  NeedsRehomingSupport: 'needs_rehoming_support',
  LikelySuccessfulTransaction: 'likely_successful_transaction',
  LikelyFailedTransaction: 'likely_failed_transaction',
  Stalled: 'stalled',
  SellerDeclined: 'seller_declined',
  BuyerDeclined: 'buyer_declined',
  PetAlreadySold: 'pet_already_sold',
  Positive: 'positive',
  Neutral: 'neutral',
  Uncertain: 'uncertain',
  Frustrated: 'frustrated',
  Disappointed: 'disappointed',
  Excited: 'excited',
} as const;

export type MessageAnnotationLabel = typeof MessageAnnotationLabel[keyof typeof MessageAnnotationLabel]
